import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { SearchService } from './search.service';
import { ScreenSizeService } from '../services/screen-size.service';
import {
    AppliedFilterIds,
    Filter,
    SortLabel,
} from '../filter-data/filter-data.model';
import {
    addFilterIdsToQueryStringAndSearch,
    addSortingDataToQueryString,
} from '../category-deals-list/category-sort-bar/category-sort-bar.utils';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import {
    createDateFromString,
    createDateString,
} from '../controls/calendar/calendar.utils';
import {
    addClassToSearchDatesComponent,
    removeClassFromSearchDatesComponent,
} from '../utilities/utils';
import { combineLatest, Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core'; // Import TranslateService
import { DeviceCommsService } from '../services/device-comms.service';
import * as Sentry from '@sentry/angular-ivy';
import { ProductCardEntry } from '@app/controls/product-card/product-card.model';
import { FilterApplicationService } from '@app/filter-data/filter-application.service';
import { ScreenScrollService } from '@app/services/screen-scroll.service';

@Component({
    selector: 'md-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
})
export class SearchComponent implements OnInit, OnDestroy {
    searchQuery = '';
    deals: ProductCardEntry[] = [];
    loading = true;
    currentPage = 1;
    totalRecords = 0;
    scrollablePane: HTMLElement;
    sortLabel: SortLabel;
    appliedFilterIds2: AppliedFilterIds[] = [];
    filterIdsFromQueryString2: AppliedFilterIds[] = [];
    productIds: number[] = [];
    sectionId: number;
    fromDate: NgbDate;
    toDate: NgbDate;
    occupancySerialized: string;

    private queryParamsSubscription: Subscription;
    private searchSubscription: Subscription;

    errorMessage = '';
    errorContact = '';

    constructor(
        public screenSizeService: ScreenSizeService,
        private route: ActivatedRoute,
        private router: Router,
        private searchService: SearchService,
        private translate: TranslateService, // Inject TranslateService
        private deviceCommsService: DeviceCommsService,
        private filterApplicationService: FilterApplicationService,
        private screenScrollService: ScreenScrollService
    ) {}

    ngOnInit(): void {
        this.queryParamsSubscription = combineLatest([
            this.route.queryParams,
            this.route.params,
        ]).subscribe(([queryParams, params]) => {
            const searchQuery: string = queryParams['q'];
            const fromDate: string = queryParams['fromDate'];
            const filterParams: string = queryParams['filters'];
            const sortParam: string = queryParams['sort'];
            const { filterIds, sortLabel } = this.setSortAndFiltering(
                filterParams,
                sortParam
            );
            this.sectionId = Number.parseInt(params['sectionId']);

            if (fromDate) {
                const { fromDate, toDate, occupancyStringSerialized } =
                    this.extractDateParams(queryParams);
                this.searchService
                    .searchForProductIds(
                        fromDate,
                        toDate,
                        occupancyStringSerialized,
                        filterIds,
                        this.sectionId
                    )
                    .pipe(
                        catchError((error) => {
                            const isTechError = error.status !== 404;
                            this.computeErrorText(isTechError);
                            this.loading = false;
                            this.deals = [];
                            return of(null);
                        })
                    )
                    .subscribe((response) => {
                        if (response) {
                            this.productIds = response;
                            if (this.productIds.length === 0) {
                                this.computeErrorText();
                                this.loading = false;
                                this.deals = [];
                                return;
                            }
                            this.search('', filterIds, sortLabel);
                        }
                    });
            } else if (searchQuery) {
                const isGibberish =
                    this.identifyGibberishAndErrorOut(searchQuery);
                if (isGibberish) return;
                this.search(searchQuery, filterIds, sortLabel);
            }
        });

        this.filterApplicationService.$filterSubject.subscribe((filters) =>
            this.applyFilters(filters)
        );

        this.scrollablePane = this.screenScrollService.getScrollablePane();
        addClassToSearchDatesComponent();
    }

    ngOnDestroy(): void {
        if (this.queryParamsSubscription) {
            this.queryParamsSubscription.unsubscribe();
        }
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
        removeClassFromSearchDatesComponent();
    }

    private setSortAndFiltering(
        filterParams: string,
        sortParams: string
    ): {
        filterIds: AppliedFilterIds[];
        sortLabel: SortLabel;
    } {
        if (filterParams) {
            this.filterIdsFromQueryString2 = filterParams
                .split(',')
                .map((filter) => {
                    const [filterTypeId, filterId] = filter.split('-');
                    return { filterTypeId: +filterTypeId, filterId: +filterId };
                });
        } else {
            this.filterIdsFromQueryString2 = [];
        }
        if (sortParams) {
            this.sortLabel = sortParams as SortLabel;
        }

        const filterIds = this.getFilterIdsFromParamsAndFilterComponent();
        return { filterIds, sortLabel: this.sortLabel };
    }

    private extractDateParams(queryParams: Params): {
        fromDate: NgbDate;
        toDate: NgbDate;
        occupancyStringSerialized: string;
    } {
        const fromDateString: string = queryParams['fromDate'];
        const toDateString: string = queryParams['toDate'];
        const occupancy: string = queryParams['occupancy'];
        this.fromDate = createDateFromString(fromDateString);
        this.toDate = createDateFromString(toDateString);
        this.occupancySerialized = occupancy;
        return {
            fromDate: this.fromDate,
            toDate: this.toDate,
            occupancyStringSerialized: occupancy,
        };
    }

    private getFilterIdsFromParamsAndFilterComponent(): AppliedFilterIds[] {
        if (this.appliedFilterIds2.length > 0) {
            return this.appliedFilterIds2;
        } else {
            return this.filterIdsFromQueryString2;
        }
    }

    private isGibberish(searchQuery: string): boolean {
        const gibberishPattern =
            /^(?![\d\s]*$)(?!.*(.)\1{2})(?!.*(.)\2\2)[^\w\d]*$/;
        const isGibberish = gibberishPattern.test(searchQuery);

        if (isGibberish) {
            Sentry.captureMessage(
                'Potential gibberish search query',
                'warning'
            );
        }

        return isGibberish;
    }

    private identifyGibberishAndErrorOut(searchQuery: string): boolean {
        if (this.isGibberish(searchQuery)) {
            this.loading = false;
            this.deals = [];
            this.errorMessage = this.translate.instant(
                "Your search looks like it's got some typos, please take another look"
            );
            Sentry.captureMessage(
                'Potential gibberish search query',
                'warning'
            );
            return true;
        }
        return false;
    }

    search(
        searchQuery: string,
        filterIds: AppliedFilterIds[],
        sortLabel: SortLabel
    ): void {
        this.loading = true;
        this.errorMessage = '';
        this.searchQuery = searchQuery;
        this.searchSubscription = this.searchService
            .searchProducts(
                this.searchQuery,
                this.productIds,
                filterIds,
                sortLabel,
                1,
                this.totalRecords,
                this.sectionId
            )
            .subscribe(
                (response) => {
                    this.deals = response.deals;
                    this.totalRecords = response.totalHits;
                    this.filterApplicationService.changeNrOfResults(
                        this.totalRecords
                    );
                    this.loading = false;
                    setTimeout(() => {
                        this.screenScrollService.scrollTo(
                            this.searchService.scrollPosition
                        );
                    }, 0);
                },
                (error) => {
                    const isTechError = error.status !== 404;
                    this.computeErrorText(isTechError);
                    this.loading = false; // Ensure loading state is reset on error
                    this.deals = []; // Clear deals on error to indicate no results
                }
            );
    }

    private computeErrorText(isTechFailure = false): void {
        const noResultsMessage = this.translate.instant(
            'Oops, no results found. Don’t worry, though, a perfect deal might just be one step away.'
        );
        const actionMessage = this.translate.instant(
            'Adjust your search criteria and try again.'
        );

        const actionTechFailureMessage =
            this.translate.instant('Contact us at');
        const contactMessagePost = this.translate.instant(
            'for personalized assistance.'
        );
        const or = this.translate.instant('or');
        this.errorMessage = `${noResultsMessage}  </br></br>`;
        this.errorContact = isTechFailure
            ? `<p>${actionTechFailureMessage} <a href="mailto:info@marideal.mu">info@marideal.mu</a> ${or} <a href="tel:+230263 2769">263 2769 </a> ${contactMessagePost} </p>`
            : `${actionMessage}`;
    }

    onScroll(): void {
        if (this.deals.length < this.currentPage * 20) {
            return;
        }
        this.currentPage++;
        this.loading = true;
        this.searchService
            .searchProducts(
                this.searchQuery,
                this.productIds,
                this.appliedFilterIds2,
                this.sortLabel,
                this.currentPage,
                this.totalRecords,
                this.sectionId
            )
            .subscribe((response) => {
                this.deals = response.deals;
                this.loading = false;
            });
    }

    redirectToProductDetails(product: ProductCardEntry) {
        this.searchService.scrollPosition =
            this.screenScrollService.getScrollPosition();
        const url = this.createUrl(product);

        this.deviceCommsService.openLinkInNewTabIfBrowser(
            url.baseUrl,
            url.queryParams
        );
    }

    private createUrl(product: ProductCardEntry): {
        baseUrl: string;
        queryParams: Params;
    } {
        let queryParams: Params = {
            search: this.searchQuery,
        };

        if (this.fromDate && this.toDate && this.occupancySerialized) {
            queryParams = {
                ...queryParams,
                fromDate: createDateString(this.fromDate),
                toDate: createDateString(this.toDate),
                occupancy: this.occupancySerialized,
            };
        }

        const baseUrl = `/deal/${product.productPath}/${product.id}`;
        return { baseUrl, queryParams };
    }

    applyFilters(filters: Filter[]) {
        this.loading = true;
        this.appliedFilterIds2 =
            filters?.flatMap((f) =>
                f.filterValues.map((fv) => ({
                    filterTypeId: f.filterTypeId,
                    filterId: fv.filterId,
                }))
            ) || [];
        this.currentPage = 1;
        addFilterIdsToQueryStringAndSearch(this.router, this.appliedFilterIds2);
    }

    applySorting(sortLabel: SortLabel) {
        this.loading = true;
        this.sortLabel = sortLabel;
        addSortingDataToQueryString(this.router, sortLabel);
        this.currentPage = 1;
    }
}
